import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { supabase } from '@/services/db/supabase';
import { useAuthStore } from '@/store/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

// Password complexity — 12+ chars, upper, lower, number, special
const passwordSchema = z
  .object({
    password: z
      .string()
      .min(12, 'At least 12 characters')
      .regex(/[A-Z]/, 'One uppercase letter')
      .regex(/[a-z]/, 'One lowercase letter')
      .regex(/[0-9]/, 'One number')
      .regex(/[^A-Za-z0-9]/, 'One special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

type PasswordForm = z.infer<typeof passwordSchema>;

interface Requirement {
  label: string;
  test: (v: string) => boolean;
}

const REQUIREMENTS: Requirement[] = [
  { label: 'At least 12 characters', test: (v) => v.length >= 12 },
  { label: 'At least one uppercase letter', test: (v) => /[A-Z]/.test(v) },
  { label: 'At least one lowercase letter', test: (v) => /[a-z]/.test(v) },
  { label: 'At least one number', test: (v) => /[0-9]/.test(v) },
  { label: 'At least one special character', test: (v) => /[^A-Za-z0-9]/.test(v) },
];

type FlowType = 'invite' | 'recovery' | null;

export default function SetupPage() {
  const navigate = useNavigate();
  const initialize = useAuthStore((s) => s.initialize);
  const [flowType, setFlowType] = useState<FlowType>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [isExchanging, setIsExchanging] = useState(true);
  const [serverError, setServerError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const passwordValue = watch('password');

  // On mount: exchange the token from the URL
  useEffect(() => {
    async function exchangeToken() {
      try {
        // Supabase sends tokens in the URL hash fragment:
        // #access_token=...&type=invite (or type=recovery)
        const hash = window.location.hash;

        if (hash) {
          // Let Supabase client handle the hash fragment automatically
          const { data, error } = await supabase.auth.getSession();

          if (error) {
            setTokenError(
              'This setup link has expired or is invalid. Contact your administrator for a new invitation.',
            );
            return;
          }

          if (data.session) {
            // Determine flow type from URL hash
            const params = new URLSearchParams(hash.substring(1));
            const type = params.get('type');

            if (type === 'recovery') {
              setFlowType('recovery');
            } else {
              setFlowType('invite');
              // Try to get first name from user metadata
              const meta = data.session.user.user_metadata;
              setFirstName(meta?.first_name ?? null);
            }
            return;
          }
        }

        // No hash — check for existing session (e.g., page reload)
        const { data: sessionData } = await supabase.auth.getSession();
        if (sessionData.session) {
          // Check if this is a recovery flow via the session event
          const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event) => {
              if (event === 'PASSWORD_RECOVERY') {
                setFlowType('recovery');
              }
              subscription.unsubscribe();
            },
          );

          // If we already have a session but no flow type, treat as invite
          setFlowType('invite');
          setFirstName(
            sessionData.session.user.user_metadata?.first_name ?? null,
          );
          return;
        }

        // No token and no session
        setTokenError(
          'This setup link has expired or is invalid. Contact your administrator for a new invitation.',
        );
      } catch {
        setTokenError(
          'This setup link has expired or is invalid. Contact your administrator for a new invitation.',
        );
      } finally {
        setIsExchanging(false);
      }
    }

    exchangeToken();
  }, []);

  const onSubmit = async (data: PasswordForm) => {
    setServerError(null);
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) {
        setServerError(error.message);
        return;
      }

      // Refresh auth store with the new session
      await initialize();

      navigate('/home', { replace: true });
    } catch {
      setServerError('Failed to set password. Try again.');
    }
  };

  // Loading state while exchanging token
  if (isExchanging) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mozaic-navy px-4">
        <Card className="w-full max-w-sm">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Verifying your setup link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Token expired or invalid
  if (tokenError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-mozaic-navy px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <h1 className="text-2xl font-bold tracking-tight text-mozaic-teal">
              MOZAIC
            </h1>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-3 text-sm text-destructive">
              {tokenError}
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/login', { replace: true })}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const heading =
    flowType === 'recovery'
      ? 'Reset Your Password'
      : firstName
        ? `Welcome to MOZAIC, ${firstName}!`
        : 'Welcome to MOZAIC';
  const subtext =
    flowType === 'recovery'
      ? 'Choose a new password for your account.'
      : 'Set up your password to activate your account.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-mozaic-navy px-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="space-y-1 text-center">
          <h1 className="text-2xl font-bold tracking-tight text-mozaic-teal">
            {heading}
          </h1>
          <p className="text-sm text-muted-foreground">{subtext}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {serverError && (
              <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {serverError}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                {...register('password')}
              />
              {errors.password && (
                <p className="text-xs text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Live password requirements checklist */}
            <ul className="space-y-1 text-xs">
              {REQUIREMENTS.map((req) => {
                const met = req.test(passwordValue);
                return (
                  <li key={req.label} className="flex items-center gap-2">
                    <span
                      className={
                        met ? 'text-emerald-500' : 'text-muted-foreground'
                      }
                    >
                      {met ? '\u2713' : '\u25CB'}
                    </span>
                    <span
                      className={
                        met ? 'text-emerald-600' : 'text-muted-foreground'
                      }
                    >
                      {req.label}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                autoComplete="new-password"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="text-xs text-destructive">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-mozaic-teal text-white hover:bg-mozaic-teal/90"
            >
              {isSubmitting ? 'Setting password...' : 'Set Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
